import SelectRedesign from 'components/Select/SelectRedesign';
import useClickOutside from 'components/utils/useClickOutside';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';
import 'react-day-picker/src/style.css';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import { isValidDate } from 'utils/dateValidation';
import { calendarDateToUtcString, utcStringToCalendarDate } from './CustomerEditO365SettingsShared';

const START_YEAR = 2020;

const MONTH_OPTIONS = [
  { label: 'January', value: '0' },
  { label: 'February', value: '1' },
  { label: 'March', value: '2' },
  { label: 'April', value: '3' },
  { label: 'May', value: '4' },
  { label: 'June', value: '5' },
  { label: 'July', value: '6' },
  { label: 'August', value: '7' },
  { label: 'September', value: '8' },
  { label: 'October', value: '9' },
  { label: 'November', value: '10' },
  { label: 'December', value: '11' },
];

const formatSyncDateLabel = (value: string): string => {
  if (value && isValidDate(value)) {
    return moment.utc(value).format('MM/DD/YYYY');
  }

  return 'Not set';
};

const buildYearOptions = (): { label: string; value: string }[] => {
  const currentYear = moment().year();
  const options: { label: string; value: string }[] = [];

  for (let year = currentYear; year >= START_YEAR; year -= 1) {
    options.push({ label: String(year), value: String(year) });
  }

  return options;
};

const calendarDateFromParts = (year: number, month: number): Date => new Date(year, month, 1);

const SyncDatePickerRow: React.FC<{
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  testId: string;
}> = ({ label, description, value, onChange, testId }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const popperElementRef = useRef<HTMLDivElement | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [popperRef, setPopperRef] = useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(anchorRef.current, popperRef, {
    placement: 'bottom-end',
  });
  const defaultClassnames = getDefaultClassNames();
  const selectedDate = useMemo(() => utcStringToCalendarDate(value), [value]);
  const yearOptions = useMemo(buildYearOptions, []);
  const todayCalendarDate = useMemo(() => {
    const today = moment.utc();
    return new Date(today.year(), today.month(), today.date());
  }, []);

  const [displayMonth, setDisplayMonth] = useState<Date>(
    () => selectedDate ?? calendarDateFromParts(moment.utc().year(), moment.utc().month())
  );
  const [selectedMonth, setSelectedMonth] = useState(() =>
    String((selectedDate ?? todayCalendarDate).getMonth())
  );
  const [selectedYear, setSelectedYear] = useState(() =>
    String((selectedDate ?? todayCalendarDate).getFullYear())
  );

  const handlePopperRef = useCallback((node: HTMLDivElement | null) => {
    popperElementRef.current = node;
    setPopperRef((current) => (current === node ? current : node));
  }, []);

  useClickOutside([anchorRef, popperElementRef], () => setShowCalendar(false));

  useEffect(() => {
    if (!showCalendar) return;

    const initialDate = selectedDate ?? todayCalendarDate;
    setDisplayMonth(initialDate);
    setSelectedMonth(String(initialDate.getMonth()));
    setSelectedYear(String(initialDate.getFullYear()));
  }, [showCalendar, value, selectedDate, todayCalendarDate]);

  const handleMonthChange = (month: string): void => {
    setSelectedMonth(month);
    setDisplayMonth(
      calendarDateFromParts(Number.parseInt(selectedYear, 10), Number.parseInt(month, 10))
    );
  };

  const handleYearChange = (year: string): void => {
    setSelectedYear(year);
    setDisplayMonth(
      calendarDateFromParts(Number.parseInt(year, 10), Number.parseInt(selectedMonth, 10))
    );
  };

  const calendarPopover = showCalendar ? (
    <div
      ref={handlePopperRef}
      className="fixed flex flex-col gap-3 w-60 px-3 pt-3 pb-2 mt-1 bg-white border border-litlingo-gray-2 rounded z-alert overflow-visible"
      style={{ ...styles.popper, filter: 'drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.25))' }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...attributes.popper}
      aria-hidden
      onClick={(event): void => event.stopPropagation()}
    >
      <div className="grid grid-cols-2 gap-2">
        <SelectRedesign
          dataTestid={`${testId}-month`}
          onChange={handleMonthChange}
          options={MONTH_OPTIONS}
          value={selectedMonth}
          dropdownClassname="z-alert"
        />
        <SelectRedesign
          dataTestid={`${testId}-year`}
          onChange={handleYearChange}
          options={yearOptions}
          value={selectedYear}
          dropdownClassname="z-alert max-h-48"
        />
      </div>
      <div className="w-full flex items-center justify-center">
        <DayPicker
          mode="single"
          month={displayMonth}
          onMonthChange={setDisplayMonth}
          hideNavigation
          selected={selectedDate}
          onSelect={(day: Date | undefined): void => {
            if (day) {
              onChange(calendarDateToUtcString(day));
              setShowCalendar(false);
            }
          }}
          disabled={{ after: todayCalendarDate }}
          classNames={{
            day: `${defaultClassnames.day} text-small`,
          }}
          modifiersClassNames={{
            disabled: 'disabled-date',
          }}
        />
      </div>
    </div>
  ) : null;

  const portalTarget = document.getElementById('dropdown-portal');

  return (
    <div className="px-5 py-4 border-t border-litlingo-gray-2">
      <div className="flex justify-between items-start gap-4">
        <div className="max-w-md">
          <p className="text text--medium">{label}</p>
          <p className="text-xs">{description}</p>
        </div>
        <div ref={anchorRef} className="flex flex-row items-center gap-2 flex-shrink-0">
          <button
            type="button"
            data-testid={testId}
            className="button button--secondary text-sm px-3 h-8"
            onClick={(): void => setShowCalendar((open) => !open)}
          >
            {formatSyncDateLabel(value)}
          </button>
          {value ? (
            <button
              type="button"
              data-testid={`${testId}-clear`}
              className="text-sm text-litlingo-primary"
              onClick={(): void => {
                onChange('');
                setShowCalendar(false);
              }}
            >
              Clear
            </button>
          ) : null}
          {portalTarget && calendarPopover
            ? ReactDOM.createPortal(calendarPopover, portalTarget)
            : calendarPopover}
        </div>
      </div>
    </div>
  );
};

export default SyncDatePickerRow;
